import { TrendingUp, Landmark, Leaf, Lock, Building2, RefreshCw, ArrowRight } from 'lucide-react';
import ScrollReveal from '@/components/ScrollReveal';

const services = [
  {
    Icon: TrendingUp,
    title: 'Conventional Bond Issuance',
    description: 'Full-spectrum advisory and arrangement for investment-grade and high-yield debt, including fixed-rate bonds, floating-rate notes (FRNs), and medium-term note (MTN) programs. We handle credit analysis, pricing, documentation, investor syndication, and execution.',
  },
  {
    Icon: Landmark,
    title: 'Sukuk & Islamic Finance',
    description: 'Sharia-compliant structuring for sovereign and corporate issuers. We establish SPVs in DIFC, ADGM, or offshore jurisdictions and build trust certificate frameworks including Ijarah, Murabaha, and Musharaka structures with full Sharia board coordination.',
  },
  {
    Icon: Leaf,
    title: 'Green & ESG Bonds',
    description: 'ICMA-aligned green bonds, social bonds, sustainability-linked bonds, and transition bonds. We guide issuers through project selection, KPI development, third-party verification, investor targeting of ESG-focused mandates, and post-issuance impact reporting.',
  },
  {
    Icon: Lock,
    title: 'Private Placements',
    description: 'Confidential, efficient capital raising through institutional private placements and Schuldschein-style structures. Faster execution, flexible terms, and direct bank financing without the public disclosure requirements of listed securities.',
  },
  {
    Icon: Building2,
    title: 'Project Finance Bonds',
    description: 'Infrastructure-backed and project-linked debt securities with bespoke credit enhancement mechanisms, covenant packages, and cash flow waterfall structures for energy, transport, and real estate developments across the region.',
  },
  {
    Icon: RefreshCw,
    title: 'Liability Management',
    description: 'Refinancing, tender offers, consent solicitations, and debt restructuring to optimize maturity profiles and capital costs. We engineer transactions that improve credit metrics while preserving stakeholder value and market access.',
  },
];

export default function Services() {
  return (
    <section id="services" className="py-20 sm:py-28" style={{ backgroundColor: '#f4f5f7' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <ScrollReveal>
          <p className="text-sm font-inter font-semibold uppercase tracking-wider mb-3" style={{ color: '#c9a84c' }}>
            Our Services
          </p>
          <h2 className="font-playfair font-bold text-3xl sm:text-4xl mb-4" style={{ color: '#0a1628' }}>
            Comprehensive Debt Capital Markets Solutions
          </h2>
          <p className="font-inter text-base sm:text-lg max-w-2xl mb-14" style={{ color: '#6b7280' }}>
            From origination through settlement, we deliver end-to-end bond structuring and arrangement services calibrated to your capital objectives.
          </p>
        </ScrollReveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => (
            <ScrollReveal key={service.title} delay={i * 80}>
              <div
                className="bg-white rounded-xl p-6 sm:p-8 h-full flex flex-col"
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  transition: 'transform 300ms ease, border-color 300ms ease, box-shadow 300ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.borderColor = '#c9a84c';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(201,168,76,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                data-testid={`service-card-${i}`}
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center mb-5"
                  style={{ backgroundColor: '#0a1628' }}
                >
                  <service.Icon className="w-6 h-6" strokeWidth={1.5} style={{ color: '#c9a84c' }} />
                </div>
                <h3 className="font-playfair font-semibold text-lg mb-3" style={{ color: '#0a1628' }}>
                  {service.title}
                </h3>
                <p className="font-inter text-sm leading-relaxed flex-1 mb-4" style={{ color: '#6b7280' }}>
                  {service.description}
                </p>
                <a
                  href="#contact"
                  onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
                  className="inline-flex items-center gap-1 font-inter font-medium text-sm"
                  style={{ color: '#c9a84c', transition: 'gap 200ms ease' }}
                  data-testid={`service-link-${i}`}
                >
                  Learn more <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
